import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface ResearchOutputFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

export default function ResearchOutputForm({ onClose, onSuccess, editData }: ResearchOutputFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState({ 
    type: false,
    version: false,
    date: false,
    file: false,
    position: false,
    content: false,
    audience: false,
    added: false,
    modified: false,
    deleted: false,
    reason: false
  });
  const [formData, setFormData] = useState({
    type: '1枚概要PDF',
    version: '',
    date: new Date().toISOString().split('T')[0],
    file: null as File | null,
    position: '',
    content: '',
    audience: '',
    added: '',
    modified: '',
    deleted: '',
    reason: '',
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [researchTheme, setResearchTheme] = useState('');
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  const outputTypes = [
    '1枚概要PDF',
    'スライドPDF',
    'スライドPPT',
    '草稿PDF',
    '文献リスト',
  ];

  useEffect(() => {
    if (editData) {
      setFormData({
        type: editData.type || '1枚概要PDF',
        version: editData.version || '',
        date: editData.date || new Date().toISOString().split('T')[0],
        file: null,
        position: editData.position || editData.summary || '',
        content: editData.content || '',
        audience: editData.audience || '',
        added: editData.added || editData.change_log || '',
        modified: editData.modified || '',
        deleted: editData.deleted || '',
        reason: editData.reason || '',
      });
      setResearchTheme(editData.research_theme || '');
    }
    fetchAvailableThemes();
  }, [editData]);

  const fetchAvailableThemes = async () => {
    try {
      const { data, error } = await supabase
        .from('research_overview')
        .select('theme')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const themes = data?.map(item => item.theme).filter(Boolean) || [];
      setAvailableThemes(themes);
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleDelete = async () => {
    if (!editData?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('research_outputs')
        .delete()
        .eq('id', editData.id);

      if (error) throw error;

      setNotification({ type: 'success', message: '成果物を削除しました' });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('削除エラー:', error);
      setNotification({ type: 'error', message: '削除に失敗しました' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setNotification({ type: 'error', message: 'ログインが必要です。再度ログインしてください。' });
      return;
    }

    if (!formData.version || !formData.position || !formData.content) {
      setNotification({ type: 'error', message: 'バージョンと概要の必須項目を入力してください' });
      return;
    }

    if (!editData && !formData.file) {
      setNotification({ type: 'error', message: 'ファイルは必須です' });
      return;
    }

    setLoading(true);

    try {
      let filePath = editData?.file || '';

      if (formData.file) {
        const fileName = `${Date.now()}_${formData.file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('research-files')
          .upload(`outputs/${fileName}`, formData.file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`ファイルアップロードエラー: ${uploadError.message}`);
        }
        filePath = uploadData.path;
      }

      const dataToSave = {
        user_id: user.id,
        type: formData.type,
        version: formData.version,
        date: formData.date,
        file: filePath,
        research_theme: researchTheme || null,
        position: formData.position,
        content: formData.content,
        audience: formData.audience || null,
        added: formData.added || null,
        modified: formData.modified || null,
        deleted: formData.deleted || null,
        reason: formData.reason || null,
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('research_outputs')
          .update(dataToSave)
          .eq('id', editData.id);

        if (error) {
          console.error('UPDATE error:', error);
          throw new Error(`更新エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '成果物を更新しました' });
      } else {
        const { error } = await supabase.from('research_outputs').insert(dataToSave);

        if (error) {
          console.error('INSERT error:', error);
          throw new Error(`保存エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '成果物を保存しました' });
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        else onClose();
      }, 1500);
    } catch (err: any) {
      console.error('保存エラー詳細:', err);
      setNotification({ type: 'error', message: `保存に失敗しました: ${err?.message || '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 md:p-8">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-file-download-line text-teal-600"></i>
          {editData ? '成果物を編集' : '成果物を追加'}
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
        {/* 基本情報 */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 sm:p-6 border-l-4 border-teal-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-teal-600"></i>基本情報
          </h3>

          <div className="space-y-4">
            {/* 関連テーマ */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">関連テーマ</label>
              </div>
              <select
                value={researchTheme}
                onChange={(e) => setResearchTheme(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              >
                <option value="">テーマを選択（任意）</option>
                {availableThemes.map((theme, index) => (
                  <option key={index} value={theme}>{theme}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">「テーマと目的」で登録したテーマを選択できます</p>
            </div>

            {/* タイプ */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">タイプ <span className="text-red-500">*</span></label>
                <button type="button" onClick={() => setShowHelp({ ...showHelp, type: !showHelp.type })} className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer text-xs">?</button>
              </div>
              {showHelp.type && (
                <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-teal-200">
                  <div className="font-semibold mb-1">📌 何を書く？</div>
                  <p>成果物の種類を選択します。</p>
                </div>
              )}
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              >
                {outputTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* バージョンと日付 - スマホ1カラム、PC2カラム */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-semibold text-slate-700">バージョン <span className="text-red-500">*</span></label>
                  <button type="button" onClick={() => setShowHelp({ ...showHelp, version: !showHelp.version })} className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer text-xs">?</button>
                </div>
                {showHelp.version && (
                  <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-teal-200">
                    <div className="font-semibold mb-1">💡 記入例</div>
                    <p>「v1.0」「v1.1」「初版」「最終版」</p>
                  </div>
                )}
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  placeholder="例：v1.0、初版"
                  required
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-semibold text-slate-700">日付 <span className="text-red-500">*</span></label>
                </div>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  required
                />
              </div>
            </div>

            {/* ファイル */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">ファイル {!editData && <span className="text-red-500">*</span>}</label>
                <button type="button" onClick={() => setShowHelp({ ...showHelp, file: !showHelp.file })} className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer text-xs">?</button>
              </div>
              {showHelp.file && (
                <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-teal-200">
                  <div className="font-semibold mb-1">📌 何をアップロード？</div>
                  <p>PDF、PowerPoint、Word形式に対応しています。</p>
                  {editData && <p className="mt-2 text-teal-700">※編集時は新しいファイルをアップロードする場合のみ選択してください。</p>}
                </div>
              )}
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                accept=".pdf,.ppt,.pptx,.doc,.docx"
                required={!editData}
              />
              <p className="text-xs text-slate-500 mt-1">PDF、PPT、DOC形式のファイルをアップロード</p>
            </div>
          </div>
        </div>

        {/* 概要 */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border-l-4 border-blue-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-file-text-line text-blue-600"></i>概要
          </h3>

          <div className="space-y-4">
            {/* この資料の位置づけ */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">この資料の位置づけ（研究のどの段階で作成したか） <span className="text-red-500">*</span></label>
                <button type="button" onClick={() => setShowHelp({ ...showHelp, position: !showHelp.position })} className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer text-xs">?</button>
              </div>
              {showHelp.position && (
                <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-blue-200">
                  <div className="font-semibold mb-1">💡 記入例</div>
                  <p>「研究計画の段階で作成した概要資料」「データ分析完了後に作成した中間報告スライド」</p>
                </div>
              )}
              <textarea
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={2}
                placeholder="〇〇の段階で作成した△△"
                required
              />
            </div>

            {/* 主な内容 */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">主な内容（何が書かれているか） <span className="text-red-500">*</span></label>
                <button type="button" onClick={() => setShowHelp({ ...showHelp, content: !showHelp.content })} className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer text-xs">?</button>
              </div>
              {showHelp.content && (
                <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-blue-200">
                  <div className="font-semibold mb-1">💡 記入例</div>
                  <p>「主な内容は研究の背景、RQ、仮説、予定している分析手法」</p>
                </div>
              )}
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={3}
                placeholder="主な内容は〇〇、△△、□□"
                required
              />
            </div>

            {/* 想定読者・用途 */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">想定読者・用途（ゼミ発表用、指導教員提出用など）</label>
                <button type="button" onClick={() => setShowHelp({ ...showHelp, audience: !showHelp.audience })} className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer text-xs">?</button>
              </div>
              {showHelp.audience && (
                <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-blue-200">
                  <div className="font-semibold mb-1">💡 記入例</div>
                  <p>「ゼミ発表用に作成。研究計画の承認を得ることを目的とする。」</p>
                </div>
              )}
              <textarea
                value={formData.audience}
                onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={2}
                placeholder="〇〇用に作成。△△を目的とする。"
              />
            </div>
          </div>
        </div>

        {/* 変更点 */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border-l-4 border-purple-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-git-commit-line text-purple-600"></i>変更点（任意）
          </h3>

          <div className="space-y-4">
            {/* 追加した内容 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">追加した内容</label>
              <textarea
                value={formData.added}
                onChange={(e) => setFormData({ ...formData, added: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                rows={3}
                placeholder="・〇〇を追加&#10;・△△を追加"
              />
            </div>

            {/* 修正した内容 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">修正した内容</label>
              <textarea
                value={formData.modified}
                onChange={(e) => setFormData({ ...formData, modified: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                rows={3}
                placeholder="・〇〇を修正&#10;・△△を変更"
              />
            </div>

            {/* 削除した内容 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">削除した内容</label>
              <textarea
                value={formData.deleted}
                onChange={(e) => setFormData({ ...formData, deleted: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                rows={3}
                placeholder="・〇〇を削除&#10;・△△を削除"
              />
            </div>

            {/* 修正の理由・背景 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">修正の理由・背景</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                rows={3}
                placeholder="〇〇の指摘を受けて修正。△△を明確にするため。"
              />
            </div>
          </div>
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {editData && (
            <button type="button" onClick={() => setShowDeleteConfirm(true)} className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer">
              削除
            </button>
          )}
          <button type="button" onClick={onClose} className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer">
            キャンセル
          </button>
          <button type="submit" disabled={loading} className="w-full sm:flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
            {loading ? '保存中...' : editData ? '更新する' : '保存'}
          </button>
        </div>
      </form>

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-slate-900 mb-2">削除の確認</h3>
            <p className="text-slate-600 mb-6">この成果物を削除してもよろしいですか？この操作は取り消せません。</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="w-full sm:flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer">
                キャンセル
              </button>
              <button onClick={handleDelete} disabled={loading} className="w-full sm:flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                {loading ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
